import React, { useState } from "react";

import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import { FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import SelectorDias from "../components/SelectorDias";
import { RNHHorarioRangoUpd } from "services/services-mia/programa-salud/personal/RNHHorarioRangoUpd";
import { diasAParams } from "../../consts";

/**
 * Modificación de un rango: se puede mover la franja y cambiar en qué días aplica.
 *
 * El horario NO se cambia desde acá: mover una franja de jornada es borrarla de una y
 * crearla en la otra. Lo que identifica al rango es su propio id más la versión.
 */
export default function UpdateModal({ updateList, horarioRango, closeModal, onSuccess }) {
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const [inRequest, setInRequest] = useState(false);
    const [dias, setDias] = useState(horarioRango?.dias ?? []);

    /**
     * Un rango sin dias no aplica nunca, asi que no se puede guardar.
     *
     * Los dias viven fuera de Formik —el ToggleButtonGroup maneja un array, no un
     * valor de campo— asi que esta regla no entra en el schema de yup: se evalua
     * aca y apaga el submit.
     */
    const sinDias = dias.length === 0;

    const handleSubmit = async (values) => {
        // Red por si el submit llega igual: con el boton apagado no deberia, pero el
        // Enter dentro de un input tambien dispara el form.
        if (sinDias) return;

        setInRequest(true);
        const respuesta = await RNHHorarioRangoUpd(credentials, {
            id: horarioRango?.id,
            version: horarioRango?.version,
            entrada: values.entrada,
            salida: values.salida,
            ...diasAParams(dias),
        });
        setInRequest(false);

        if (!respuesta.ok) return;

        setSnackbarInfo({
            message: "Rango horario modificado correctamente",
            severity: "success",
            open: true,
        });
        updateList();
        onSuccess?.();
        closeModal();
    };

    return (
        <Formik
            initialValues={{
                // De qué jornada es esta franja. Importa sobre todo en la vista
                // suelta, donde conviven las de todos los horarios.
                horario: horarioRango?.horario ?? "",
                entrada: horarioRango?.entrada ?? "",
                salida: horarioRango?.salida ?? "",
            }}
            validationSchema={yup.object({
                entrada: yup.string().required(FORM_ERROR_MESSAGES.required),
                salida: yup.string().required(FORM_ERROR_MESSAGES.required),
            })}
            onSubmit={handleSubmit}
        >
            <Form className={styles.inputs} noValidate>
                <div className={styles["column"]}>
                    <Grid
                        container
                        rowSpacing={0}
                        columnSpacing={2}
                        sx={{
                            width: "100%",
                            "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
                        }}
                    >
                        <Grid item xs={12}>
                            {/* El horario no se cambia desde acá: mover una franja de
                                jornada es borrarla de una y crearla en la otra. */}
                            <FormikTextInput fullWidth name="horario" labelText="Horario" readOnly />
                        </Grid>
                        <Grid item xs={12}>
                            <SelectorDias
                                value={dias}
                                onChange={(_, nuevos) => setDias(nuevos)}
                                helperText={
                                    sinDias
                                        ? "Elegí al menos un día, o eliminá el rango"
                                        : undefined
                                }
                            />
                        </Grid>
                        {/* Input NATIVO de hora. Lo muestra en 12 o en 24 horas
                            segun el idioma del navegador, y no hay forma de forzarlo:
                            no existe un atributo de formato, y el `lang` del documento
                            —que ya esta en "es"— lo ignora. Se probo el TimePicker de
                            `@mui/lab` y resulto ser un cascaron deprecado que devuelve
                            `null`; los pickers vivos estan en `@mui/x-date-pickers`,
                            que no esta instalado.

                            Lo que SE GUARDA es siempre "HH:mm", muestre lo que muestre. */}
                        <Grid item xs={6}>
                            <FormikTextInput fullWidth name="entrada" labelText="Entrada" type="time" />
                        </Grid>
                        <Grid item xs={6}>
                            <FormikTextInput fullWidth name="salida" labelText="Salida" type="time" />
                        </Grid>
                    </Grid>
                    <FormButton
                        inRequest={inRequest}
                        submitDisabled={sinDias}
                        newFormData={{ button: "Guardar cambios" }}
                        onCloseModal={closeModal}
                    />
                    {inRequest && <SpinnerOverlay />}
                </div>
            </Form>
        </Formik>
    );
}
